import React, {
  forwardRef,
  useState,
  useEffect,
  useMemo,
  AnchorHTMLAttributes,
  ReactNode
} from 'react';
import Link from 'next/link';
import cx from 'classnames';

import { appendParams } from '../../utils/buildWebRegUrl';
import { maintainRegionalContext } from '../../lib/geolocation/linkUtils';
import {
  getColorTokenValue,
  getTextColorClass
} from '../../utils/colorTokenToTailwind';
import { ButtonColorTokenProp } from '../../theme/tokens/button-color';
import { CFColorTokenProp } from '../../theme/tokens/contentful-color';
import { ColorTokenProp } from '../../theme/tokens/color';
import { FontWeightProp } from '../../theme/tokens/font-weight';
import { TokenProp } from '../../theme/tokens/types';

export type Ref = HTMLAnchorElement | null;
export interface BaseLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  children?: ReactNode;
  attachParams?: boolean;
  forwardedButtonParams?: string;
}

interface LinkButtonProps extends BaseLinkProps {
  buttonColor?: ButtonColorTokenProp;
  buttonHoverColor?: ButtonColorTokenProp;
  cfButtonColor?: CFColorTokenProp;
  cfButtonHoverColor?: CFColorTokenProp;
}

interface InlineAnchorProps extends BaseLinkProps {
  colour?: ColorTokenProp | string;
  fontWeight?: FontWeightProp;
}

const getTokenValue = <T,>(
  token: TokenProp<T> | string | undefined
): string | undefined => {
  if (!token) return undefined;
  if (typeof token === 'string') return token;
  if (typeof token === 'object' && !Array.isArray(token)) {
    const tokenObj = token as Record<string, T | undefined>;
    return (tokenObj['0'] ?? tokenObj.tablet ?? tokenObj.desktop) as
      | string
      | undefined;
  }
  return undefined;
};

const getButtonColorClasses = (
  buttonColor?: ButtonColorTokenProp,
  cfButtonColor?: CFColorTokenProp
) => {
  const cfColor = getTokenValue(cfButtonColor);
  if (cfColor) {
    const cfColorMap: Record<string, { bg: string; text: string }> = {
      sweetmint: { bg: 'bg-navy', text: 'text-white' },
      spearmint: { bg: 'bg-spearmint', text: '' },
      cinnamint: { bg: 'bg-cinnamint', text: '' },
      blackberry: { bg: 'bg-black', text: 'text-white' },
      white: { bg: 'bg-white', text: '' },
      ginger: { bg: 'bg-ginger', text: '' },
      green: { bg: 'bg-green', text: '' },
      limegreen: { bg: 'bg-limegreen', text: '' }
    };
    return cfColorMap[cfColor] || { bg: '', text: '' };
  }

  const color = getTokenValue(buttonColor) || 'dark';
  if (color === 'dark') {
    return { bg: 'bg-navy', text: 'text-white' };
  }
  if (color === 'light') {
    return { bg: 'bg-green', text: 'text-black' };
  }
  return { bg: 'bg-navy', text: 'text-white' };
};

const getButtonHoverColorClasses = (
  buttonHoverColor?: ButtonColorTokenProp,
  cfButtonHoverColor?: CFColorTokenProp
) => {
  const cfColor = getTokenValue(cfButtonHoverColor);
  if (cfColor) {
    const cfColorMap: Record<string, string> = {
      sweetmint: 'hover:bg-navy',
      spearmint: 'hover:bg-spearmint',
      cinnamint: 'hover:bg-cinnamint',
      blackberry: 'hover:bg-black',
      white: 'hover:bg-white',
      ginger: 'hover:bg-ginger',
      green: 'hover:bg-green',
      limegreen: 'hover:bg-limegreen'
    };
    return cfColorMap[cfColor] || '';
  }

  const color = getTokenValue(buttonHoverColor);
  if (color === 'darkHover') {
    return 'hover:bg-green';
  }
  if (color === 'lightHover') {
    return 'hover:bg-ginger';
  }
  return 'hover:bg-green';
};

const getInlineAnchorColorClasses = (
  colour?: ColorTokenProp | string
): {
  base: string;
  tablet?: string;
  desktop?: string;
  hover: string;
  desktopHover: string;
} => {
  const baseColor = getColorTokenValue(colour) || 'defaultText';
  const tabletColor =
    typeof colour === 'object' && !Array.isArray(colour)
      ? (colour as Record<string, string | undefined>).tablet
      : undefined;
  const desktopColor =
    typeof colour === 'object' && !Array.isArray(colour)
      ? (colour as Record<string, string | undefined>).desktop
      : undefined;

  const baseClass = getTextColorClass(baseColor as ColorTokenProp | string);
  const tabletClass = tabletColor
    ? getTextColorClass(tabletColor as ColorTokenProp | string, baseClass)
    : undefined;
  const desktopClass = desktopColor
    ? getTextColorClass(desktopColor as ColorTokenProp | string, baseClass)
    : undefined;

  const isDefaultText =
    baseColor === 'defaultText' || desktopColor === 'defaultText';
  const hoverClass = 'hover:text-navy';
  const desktopHoverClass = isDefaultText
    ? 'desktop:hover:text-navy'
    : 'desktop:hover:text-green';

  return {
    base: baseClass,
    tablet: tabletClass,
    desktop: desktopClass,
    hover: hoverClass,
    desktopHover: desktopHoverClass
  };
};

const getFontWeightClass = (fontWeight?: FontWeightProp): string => {
  if (!fontWeight) return '';
  const weight =
    typeof fontWeight === 'string'
      ? fontWeight
      : (fontWeight as Record<string, string | undefined>)['0'] ??
        (fontWeight as Record<string, string | undefined>).tablet;

  if (!weight) return '';

  const weightMap: Record<string, string> = {
    '100': 'font-thin',
    '200': 'font-extralight',
    '300': 'font-light',
    '400': 'font-normal',
    '500': 'font-medium',
    '600': 'font-semibold',
    '700': 'font-bold',
    '800': 'font-extrabold',
    '900': 'font-black',
    inlineAnchor: 'font-semibold',
    p: 'font-normal'
  };

  return weightMap[weight] || '';
};

const GlLink = forwardRef<Ref, BaseLinkProps>(function GlLink(
  {
    href,
    children,
    attachParams = false,
    forwardedButtonParams,
    className,
    ...linkProps
  }: BaseLinkProps,
  ref
) {
  // Treat same-page anchors ('#...') and single-slash paths as internal
  const internal = useMemo(
    () => (href as string)?.startsWith('#') || /^\/(?!\/)/.test(href as string),
    [href]
  );

  // Apply regional context to internal links
  const regionalHref = useMemo(() => {
    if (internal && href) {
      return maintainRegionalContext(href as string);
    }
    return href as string;
  }, [internal, href]);

  const [url, setUrl] = useState<string>(regionalHref);

  useEffect(() => {
    if (!internal && attachParams) {
      const urlWithParams = appendParams({
        baseUrl: regionalHref,
        phoneNumber: '',
        bypassPhoneNumberEntry: true,
        forwardedButtonParams
      });
      setUrl(urlWithParams);
    } else {
      setUrl(regionalHref);
    }
  }, [attachParams, regionalHref, internal]);

  return (
    <Link href={url} prefetch={false} passHref legacyBehavior>
      <a
        ref={ref}
        data-gl-link="greenlight-link"
        data-testid="greenlight-link"
        className={className}
        {...linkProps}
      >
        {children}
      </a>
    </Link>
  );
});

export default GlLink;
export { GlLink };

export const LinkButton = forwardRef<Ref, LinkButtonProps>(function LinkButton(
  {
    href,
    children,
    attachParams = false,
    forwardedButtonParams,
    className,
    buttonColor,
    buttonHoverColor,
    cfButtonColor,
    cfButtonHoverColor,
    ...linkProps
  }: LinkButtonProps,
  ref
) {
  const colorClasses = getButtonColorClasses(buttonColor, cfButtonColor);
  const hoverClass = getButtonHoverColorClasses(
    buttonHoverColor,
    cfButtonHoverColor
  );

  return (
    <GlLink
      ref={ref}
      href={href}
      attachParams={attachParams}
      forwardedButtonParams={forwardedButtonParams}
      className={cx(
        'inline-flex h-9 min-w-[146px] cursor-pointer appearance-none flex-col justify-center rounded-lg border-0 px-3 text-sm font-medium no-underline outline-none transition-colors duration-200',
        'tablet:text-base desktop:h-10 desktop:min-w-[168px]',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-limegreen',
        colorClasses.bg,
        colorClasses.text,
        hoverClass,
        className
      )}
      {...linkProps}
    >
      {children}
    </GlLink>
  );
});

export const InlineAnchor = forwardRef<Ref, InlineAnchorProps>(
  function InlineAnchor(
    {
      href,
      children,
      attachParams = false,
      forwardedButtonParams,
      className,
      colour,
      fontWeight,
      ...linkProps
    }: InlineAnchorProps,
    ref
  ) {
    const colorClasses = getInlineAnchorColorClasses(colour);
    const fontWeightClass = getFontWeightClass(fontWeight);

    return (
      <GlLink
        ref={ref}
        href={href}
        attachParams={attachParams}
        forwardedButtonParams={forwardedButtonParams}
        className={cx(
          'underline transition-colors duration-200',
          colorClasses.base,
          colorClasses.tablet ? `tablet:${colorClasses.tablet}` : '',
          colorClasses.desktop ? `desktop:${colorClasses.desktop}` : '',
          colorClasses.hover,
          colorClasses.desktopHover,
          fontWeightClass,
          className
        )}
        {...linkProps}
      >
        {children}
      </GlLink>
    );
  }
);
